import './rule-editor-applications.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EditableRule, MultiSelectType } from '../../../../../model/EditableRule';
import EntitySelector, { DefaultLabel, ListItem } from '../../../../shared/entity-selector/entity-selector';
import { observer } from 'mobx-react-lite';
import { useGenericHandler } from '../useGenericandler';
import { FieldMessage } from '../../../../../dtos/error-message.dto';
import { isAllowedRule } from '../../../../../utils/ruleUtils';
import { keyBy, merge } from 'lodash';
import Alert from '../../../../shared/alert/alert';
import managedAppsService from '../../../../../services/managed-apps.service';
import { ManagedAppsSelectModal } from '../../../apps-services/managed-apps-select/managed-apps-select';
import CustomIcon, { IconType } from '../../../../shared/custom-icon/custom-icon';
import { Button } from 'antd';
import Loader from '../../../../shared/loader/loader';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';
import { AppDto, ApplicationMetadata } from '../../../../../dtos/app.dto';
import { DependenciesWarningModal } from '../../../apps-services/warnings/dependencies-warning-modal';
import { arrayToMap } from '../../../../../utils/map-and-array';

interface RuleEditorApplicationsProps {
    ruleData: EditableRule;
    sectionErrors: { [field: string]: FieldMessage };
    handleRuleChange: (ruleData: Partial<EditableRule>) => void
}

export const RuleEditorApplications: React.FC<RuleEditorApplicationsProps> = observer(({ ruleData,
    sectionErrors,
    handleRuleChange }) => {

    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isLoading, setIsLoading] = useState(false);
    const [isAppsSelectOpen, setIsAppsSelectOpen] = useState(false);
    const [appToRemove, setAppToRemove] = useState<ListItem>();
    const [dependentApps, setDependentApps] = useState<string[]>([]);
    const [fullApplicationList, setFullApplicationList] = useState<ListItem[]>([]);

    useEffect(() => {
        addAllExistingApps(ruleData.applications);
    }, []);

    useEffect(() => {
        applicationHandler.handleAllItemsChange(fullApplicationList.map(item => item.key), true);
    }, [fullApplicationList]);

    const selectedAppIds = useMemo(() => arrayToMap(fullApplicationList, _ => _.key, _ => _.name),
        [fullApplicationList]);

    const applicationHandler = useGenericHandler(
        fullApplicationList,
        ruleData.applications,
        'key',
        'applications',
        handleRuleChange
    );

    const resetApplications = useCallback(() => {
        if (ruleData.applicationsType !== MultiSelectType.ANY) {
            applicationHandler.removeAllItems();
            applicationHandler.handleTypeChange(MultiSelectType.ANY);
        }
    }, [applicationHandler, ruleData.applicationsType]);

    const addAllExistingApps = (appIds: string[]) => {
        if (appIds.length > 0) {
            setIsLoading(true);
            promiseFeedbackFactory({
                promise: managedAppsService.getApplicationsIfExist(appIds),
                successCallback: (apps: AppDto[]) => {
                    setIsLoading(false);
                    setFullApplicationList(apps
                        .map(app => ({key: app.applicationMetadata.id, name: app.applicationMetadata.displayName, data: {dep: app.dependencies}}))
                    );
                },
                errorCallback: () => {
                    setIsLoading(false);
                },
                notifObj: {show: false}
            });
        }
    };

    const addSelectedApps = (selectedList: ListItem[]) => {
        const appsToAdd: ListItem[] = [...selectedList];

        if (isAllowedRule(ruleData.action)) {
            selectedList.forEach(app => app.data.dep
                .forEach((dep: ApplicationMetadata) =>
                    appsToAdd.push({key: dep.id, name: dep.displayName, data: {dep: []}})));
        }

        calculateFullListOfApps(appsToAdd);
        setIsAppsSelectOpen(false);
    };

    const calculateFullListOfApps = (appsToAdd: ListItem[]) => {
        const fullApplicationListByKey = keyBy(fullApplicationList, 'key');
        const combinedMap = merge(fullApplicationListByKey, keyBy(appsToAdd, 'key'));
        setFullApplicationList(Object.values(combinedMap));
    };

    const closeDependentAppsWarning = () => {
        setAppToRemove(undefined);
        setDependentApps([]);
    };

    const removeItem = (appId: string) => {
        applicationHandler.removeItem(appId);
        setFullApplicationList(fullApplicationList.filter(app => app.key !== appId));
    };

    const onRemoveAppOk = (appId: string) => {
        removeItem(appId);
        closeDependentAppsWarning();
    };

    const getDependentApps = (appId: string): string[] => {
        return fullApplicationList
            .filter(app => app.data?.dep?.some((dep: ApplicationMetadata) => dep.id === appId))
            .map(app => app.name);
    };

    const handleRemoveApp = (appId: string) => {
        if (isAllowedRule(ruleData.action)) {
            const app = fullApplicationList.find(_ => _.key === appId);
            if (app) {
                const dependencies = getDependentApps(app.key);
                if (dependencies.length) {
                    setAppToRemove(app);
                    setDependentApps(dependencies);
                } else {
                    removeItem(appId);
                }
            }
        } else {
            removeItem(appId);
        }
    };

    const isDisabled = useCallback(() => {
        if (!ruleData.isDefault) {
            if (ruleData.servicesType === MultiSelectType.SELECTED) {
                resetApplications();
                return true;
            }
            return false;
        }

        return true;
    }, [ruleData.isDefault, ruleData.servicesType, ruleData.name]);

    const AddButton = (
            <Button className={'button-link add-entity-button add-apps-button'}
                    onClick={() => setIsAppsSelectOpen(true)}>
                <CustomIcon type={IconType.PLUS} size={10}/>
                {`Add Apps`}
            </Button>
    );

    return <div className={'rule-editor-applications'}>
        {isLoading && <Loader type="full-screen"/>}
        <EntitySelector
            entityName={{singular: 'App', plural: 'Apps'}}
            fullList={fullApplicationList.map(item => ({key: item.key, name: item.name, data: item.name}))}
            selectedItems={ruleData.applications}
            error={sectionErrors['applicationsType']}
            handleSelection={applicationHandler.handleItemChange}
            handleRemove={handleRemoveApp}
            handleTypeSelection={applicationHandler.handleTypeChange}
            type={ruleData.applicationsType}
            isTypeDisabled={isDisabled()}
            EntityLabel={({data}) => <DefaultLabel label={data.toUpperCase()}/>}
            AddButton={AddButton}
            WarningMessage={ruleData.applications.length > 200 ?
                <Alert message={'Maximum apps reached. Please select up to 200.'} isError={true} /> : null}
        />
        <ManagedAppsSelectModal isOpen={isAppsSelectOpen} setIsOpen={setIsAppsSelectOpen}
                                selectedAppIds={selectedAppIds}
                                saveApps={addSelectedApps}
                                className={'rule-editor-apps-select'}
                                alertMessage={'Selected apps may have dependent apps which will also be allowed.'}
                                shouldDisplayAlert={isAllowedRule(ruleData.action)}
                                emptyStateMessage={'Search apps to add to your apps list.'}
        />
        { appToRemove && dependentApps.length > 0 &&
            <DependenciesWarningModal open={!!appToRemove} setOpen={closeDependentAppsWarning}
                                      action={'remove'} appName={appToRemove.name.toUpperCase()}
                                      dependentApps={dependentApps}
                                      okCallback={() => onRemoveAppOk(appToRemove?.key)}
            />
        }
        {promiseContextHolder}
    </div>;

});